import React, { useState } from 'react'
import toast from 'react-hot-toast'
import { folderApi } from '../../../../shared/services/folderApi'
import type { FolderNode } from './types'
import { useTranslation } from 'react-i18next'

interface CreateFolderModalProps {
  onClose: () => void
  onFolderCreated: (parentId?: string) => void
  folders: FolderNode[]
  defaultParent?: string | null
}

/** 새 폴더 생성 모달 */
const CreateFolderModal: React.FC<CreateFolderModalProps> = ({
  onClose,
  onFolderCreated,
  folders,
  defaultParent,
}) => {
  const { t } = useTranslation()
  const [name, setName] = useState('')
  const [color, setColor] = useState('#3B82F6')
  const [parentFolder, setParentFolder] = useState<string>(defaultParent || '')
  const [loading, setLoading] = useState(false)

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    if (!name.trim()) {
      return
    }

    setLoading(true)
    try {
      await folderApi.create({
        name: name.trim(),
        color,
        parentFolder: parentFolder || undefined,
      })
      onFolderCreated(parentFolder || undefined)
    } catch (error) {
      console.error('폴더 생성 실패:', error)
      toast.error(
        error instanceof Error ? error.message : t('form.createFolderError')
      )
    } finally {
      setLoading(false)
    }
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50">
      <div className="w-96 rounded-lg bg-white p-6 dark:bg-gray-800">
        <h3 className="mb-4 text-lg font-semibold text-gray-900 dark:text-white">
          {t('form.createFolder')}
        </h3>
        <form onSubmit={handleSubmit}>
          <div className="mb-4">
            <label className="mb-2 block text-sm font-medium text-gray-700 dark:text-gray-300">
              {t('form.folderName')}
            </label>
            <input
              type="text"
              value={name}
              onChange={e => setName(e.target.value)}
              className="w-full rounded-md border border-gray-300 px-3 py-2 focus:border-blue-500 focus:ring-blue-500 dark:border-gray-600 dark:bg-gray-700 dark:text-white"
              placeholder={t('form.folderNamePlaceholder')}
              autoFocus
            />
          </div>

          {/* 부모 폴더 선택 */}
          <div className="mb-4">
            <label className="mb-2 block text-sm font-medium text-gray-700 dark:text-gray-300">
              {t('form.parentFolder')}
            </label>
            <select
              value={parentFolder}
              onChange={e => setParentFolder(e.target.value)}
              className="w-full rounded-md border border-gray-300 px-3 py-2 focus:border-blue-500 focus:ring-blue-500 dark:border-gray-600 dark:bg-gray-700 dark:text-white"
            >
              <option value="">루트 폴더</option>
              {folders.map(folder => (
                <option key={folder._id} value={folder._id}>
                  {folder.name}
                </option>
              ))}
            </select>
          </div>

          <div className="mb-6">
            <label className="mb-2 block text-sm font-medium text-gray-700 dark:text-gray-300">
              {t('form.color')}
            </label>
            <div className="space-y-3">
              {/* 기본 색상 팔레트 */}
              <div className="grid grid-cols-8 gap-2">
                {[
                  '#3B82F6',
                  '#10B981',
                  '#F59E0B',
                  '#EF4444',
                  '#8B5CF6',
                  '#06B6D4',
                  '#F97316',
                  '#84CC16',
                  '#EC4899',
                  '#6366F1',
                  '#14B8A6',
                  '#F43F5E',
                  '#8E4EC6',
                  '#059669',
                  '#DC2626',
                  '#7C3AED',
                  '#0891B2',
                  '#EA580C',
                  '#65A30D',
                  '#BE185D',
                  '#4F46E5',
                  '#0D9488',
                  '#B91C1C',
                  '#7C2D12',
                ].map(c => (
                  <button
                    key={c}
                    type="button"
                    onClick={() => setColor(c)}
                    className={`h-8 w-8 rounded-full border-2 transition-all hover:scale-110 ${
                      color === c
                        ? 'border-gray-900 shadow-lg dark:border-gray-100'
                        : 'border-gray-300 dark:border-gray-600'
                    }`}
                    style={{ backgroundColor: c }}
                    title={c}
                  />
                ))}
              </div>

              {/* 파스텔 톤 */}
              <div className="grid grid-cols-8 gap-2">
                {[
                  '#DBEAFE',
                  '#D1FAE5',
                  '#FEF3C7',
                  '#FEE2E2',
                  '#EDE9FE',
                  '#CFFAFE',
                  '#FFEDD5',
                  '#ECFDF5',
                  '#FCE7F3',
                  '#E0E7FF',
                  '#CCFBF1',
                  '#FFE4E6',
                  '#F3E8FF',
                  '#A7F3D0',
                  '#FECACA',
                  '#C7D2FE',
                  '#99F6E4',
                  '#FED7AA',
                  '#D9F99D',
                  '#F9A8D4',
                  '#A5B4FC',
                  '#5EEAD4',
                  '#FCA5A5',
                  '#DDD6FE',
                ].map(c => (
                  <button
                    key={c}
                    type="button"
                    onClick={() => setColor(c)}
                    className={`h-8 w-8 rounded-full border-2 transition-all hover:scale-110 ${
                      color === c
                        ? 'border-gray-900 shadow-lg dark:border-gray-100'
                        : 'border-gray-300 dark:border-gray-600'
                    }`}
                    style={{ backgroundColor: c }}
                    title={c}
                  />
                ))}
              </div>

              {/* 커스텀 색상 선택 */}
              <div className="flex items-center space-x-3 border-t border-gray-200 pt-2 dark:border-gray-600">
                <label className="text-sm text-gray-600 dark:text-gray-400">
                  커스텀:
                </label>
                <input
                  type="color"
                  value={color}
                  onChange={e => setColor(e.target.value)}
                  className="h-8 w-10 cursor-pointer rounded border border-gray-300 dark:border-gray-600"
                  title={t('form.customColor')}
                />
                <span className="font-mono text-xs text-gray-500 dark:text-gray-400">
                  {color.toUpperCase()}
                </span>
              </div>
            </div>
          </div>
          <div className="flex justify-end space-x-3">
            <button
              type="button"
              onClick={onClose}
              className="rounded-md border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50 dark:border-gray-600 dark:bg-gray-700 dark:text-gray-300 dark:hover:bg-gray-600"
            >
              {t('common.cancel')}
            </button>
            <button
              type="submit"
              disabled={loading || !name.trim()}
              className="rounded-md border border-transparent bg-blue-600 px-4 py-2 text-sm font-medium text-white hover:bg-blue-700 disabled:opacity-50"
            >
              {loading ? t('form.creating') : t('form.createFolderSubmit')}
            </button>
          </div>
        </form>
      </div>
    </div>
  )
}

export default CreateFolderModal
